/*
 * The "first alert" card (partials/_first_alert_card.html): three one-tap BTCUSDT alerts for an
 * account that has none. Shown on the chart's Alerts panel and on /dashboard, so it is registered
 * by both bundles (entries/chart.js and entries/app.js).
 *
 * The browser sends only the option id to POST /api/first-alert/create; the server computes the
 * level from the newest candle and saves through the same save_alarms() as the builder. The
 * sentences come from the server too (translated there).
 *
 * On success it dispatches:
 *   'first-alert-created' {strategy}  -- the chart adds the row to its list (entries/chart.js)
 *   'alert-saved'                     -- the chart's Telegram step opens when no chat is linked
 *                                        (components/telegram-step.js); /dashboard has no such
 *                                        panel, so the card shows its own "Connect Telegram" link.
 */
import { api } from '../lib/api.js';
import { track } from '../lib/track.js';

const DISMISS_KEY = 'firstAlertCardDismissed';
const DAY_MS = 24 * 60 * 60 * 1000;

function dismissedBefore() {
  try { return localStorage.getItem(DISMISS_KEY) === '1'; } catch (e) { return false; }
}

export function firstAlertCard() {
  return {
    state: 'loading',        // loading | error | ready | created | hidden
    options: [],
    saving: '',              // the option id being saved
    failure: '',             // a sentence from the server when a save was refused
    created: null,           // { id, sentence, option }
    firedCount: null,        // past-data check for the RSI alert; null = not shown
    undone: false,
    canDismiss: false,
    telegramLinked: true,

    init() {
      this.canDismiss = this.$el.dataset.dismissible === '1';
      this.telegramLinked = this.$el.dataset.linked === '1';
      if (this.canDismiss && dismissedBefore()) { this.state = 'hidden'; return; }
      this.load();
    },

    async load() {
      this.state = 'loading';
      try {
        const d = await api.get('/api/first-alert/options');
        this.options = d.options || [];
        this.state = 'ready';
        track('alert.card_view');
      } catch (e) {
        console.error('could not load the first alert options', e);
        this.state = 'error';
      }
    },

    dismiss() {
      track('alert.card_dismiss');
      try { localStorage.setItem(DISMISS_KEY, '1'); } catch (e) { /* it just shows again next time */ }
      this.state = 'hidden';
    },

    async pick(option) {
      if (this.saving) return;
      this.saving = option.id;
      this.failure = '';
      track('alert.card_pick', { option: option.id });
      try {
        const d = await api.post('/api/first-alert/create', { option: option.id });
        if (!d || !d.success) {
          // plan limits and the like come back as 200 {success:false, message}
          this.failure = (d && d.message) || window.t('The alert could not be saved.');
          if (d && d.error === 'plan_limit') option.available = false;
          return;
        }
        this.created = { id: d.strategy.id, sentence: d.sentence, option: d.option };
        this.state = 'created';
        document.dispatchEvent(new CustomEvent('first-alert-created', { detail: { strategy: d.strategy } }));
        document.dispatchEvent(new CustomEvent('alert-saved'));
        if (d.option === 'rsi') this.checkPast(d.strategy.id);
      } catch (e) {
        console.error('could not create the first alert', e);
        this.failure = (e && e.payload && e.payload.message) || window.t('The alert could not be saved.');
      } finally {
        this.saving = '';
      }
    },

    // "On past data this would have fired N times in the last 30 days": the existing simulator,
    // for the one alert it means something for. A failure just hides the line.
    async checkPast(id) {
      try {
        const rows = await api.get(api.path('/simulatealarm', id));
        if (!Array.isArray(rows)) return;
        const since = Date.now() - 30 * DAY_MS;
        this.firedCount = rows.filter((r) => Number(r.timestamp) >= since).length;
      } catch (e) { /* the backend may be down; the alert itself is saved */ }
    },

    pastText() {
      if (this.firedCount === null) return '';
      if (this.firedCount === 0) return window.t('On past data this would not have fired in the last 30 days.');
      if (this.firedCount === 1) return window.t('On past data this would have fired once in the last 30 days.');
      return window.t('On past data this would have fired {n} times in the last 30 days.', { n: this.firedCount });
    },

    async undo() {
      if (!this.created || this.undone) return;
      try {
        await api.post(api.path('/deletestrategy', this.created.id));
        this.undone = true;
        document.dispatchEvent(new CustomEvent('first-alert-undone', { detail: { id: this.created.id } }));
        this.created = null;
        await this.load();
        this.undone = false;
      } catch (e) {
        console.error('could not undo the first alert', e);
        api.notify(e, 'Could not undo the alert.');
      }
    },
  };
}
